import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Spinner,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";

interface City {
  id: string;
  name: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

const emptyForm = {
  id: "" as string | null,
  name: "",
  state: "",
  latitude: "" as string | number,
  longitude: "" as string | number,
  isActive: true,
  displayOrder: 100,
};

const Cities: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      const response = await adminService.getCities();
      if (response.success && response.data) {
        setCities(response.data as City[]);
      }
    } catch (error) {
      toast.error("Failed to load cities");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (city: City) => {
    setForm({
      id: city.id,
      name: city.name,
      state: city.state,
      latitude: city.latitude ?? "",
      longitude: city.longitude ?? "",
      isActive: city.isActive,
      displayOrder: city.displayOrder ?? 100,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !form.name ||
      !form.state ||
      form.latitude === "" ||
      form.longitude === ""
    ) {
      toast.error("Name, state, latitude and longitude are required");
      return;
    }

    const payload = {
      name: String(form.name).trim(),
      state: String(form.state).trim(),
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
      isActive: form.isActive,
      displayOrder: Number(form.displayOrder) || 100,
    };

    try {
      setSaving(true);
      const response = form.id
        ? await adminService.updateCity(form.id, payload)
        : await adminService.createCity(payload);

      if (response.success) {
        toast.success(form.id ? "City updated" : "City created");
        closeModal();
        loadCities();
      } else {
        toast.error(response.error || "Failed to save city");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to save city");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (city: City) => {
    if (
      !window.confirm(
        `Deactivate city "${city.name}"? It will be hidden from the city picker.`,
      )
    ) {
      return;
    }
    try {
      const response = await adminService.deleteCity(city.id);
      if (response.success) {
        toast.success("City deactivated");
        loadCities();
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to deactivate city");
    }
  };

  const handleToggleActive = async (city: City) => {
    try {
      const response = await adminService.updateCity(city.id, {
        isActive: !city.isActive,
      });
      if (response.success) {
        loadCities();
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to update city");
    }
  };

  const filtered = cities.filter((c) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) || c.state.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <Helmet>
        <title>Cities | My Cinemas Admin</title>
      </Helmet>

      <div className="container-fluid py-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h2 className="mb-0">Cities</h2>
            <small className="text-muted">
              Manage the city list shown in the website's city picker.
            </small>
          </Col>
          <Col xs="auto">
            <Button variant="primary" onClick={openAdd}>
              <i className="bi bi-plus-lg me-2"></i>
              Add City
            </Button>
          </Col>
        </Row>

        <Card>
          <Card.Body>
            <Form.Control
              type="text"
              placeholder="Search by name or state..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-3"
            />

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>State</th>
                    <th>Latitude</th>
                    <th>Longitude</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th style={{ width: 180 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        No cities found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((city) => (
                      <tr key={city.id}>
                        <td>{city.name}</td>
                        <td>{city.state}</td>
                        <td>{city.latitude ?? "-"}</td>
                        <td>{city.longitude ?? "-"}</td>
                        <td>{city.displayOrder}</td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`active-${city.id}`}
                            checked={city.isActive}
                            onChange={() => handleToggleActive(city)}
                            label={city.isActive ? "Active" : "Hidden"}
                          />
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => openEdit(city)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(city)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{form.id ? "Edit City" : "Add City"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>State *</Form.Label>
              <Form.Control
                type="text"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                required
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Latitude *</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={form.latitude}
                    onChange={(e) =>
                      setForm({ ...form, latitude: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Longitude *</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={form.longitude}
                    onChange={(e) =>
                      setForm({ ...form, longitude: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        displayOrder: Number(e.target.value),
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Check
                    type="switch"
                    id="city-active"
                    label="Active (visible in city picker)"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default Cities;
